import type { RecognitionDetectedNote, RecognitionFrame } from 'AudioRecognition/types';

const MAX_AUDIO_CAPTURE_NOTES = 8;
const AUDIO_CAPTURE_RELATIVE_SCORE_RATIO = 0.58;
const AUDIO_CAPTURE_RELATIVE_ENERGY_RATIO = 0.45;
const FREE_CAPTURE_MIN_NOISE_FLOOR_RATIO = 2.5;
const FREE_CAPTURE_ADDITIONAL_COUNT_RATIO = 0.65;
const FREE_CAPTURE_ADDITIONAL_SCORE_SUM_RATIO = 0.6;
const FREE_CAPTURE_ADDITIONAL_MAX_SCORE_RATIO = 0.65;
const FREE_CAPTURE_ADDITIONAL_FIRST_SEEN_GRACE_MS = 90;

export type AudioCaptureCandidate = {
  midi: number;
  score: number;
  normalizedEnergy: number;
  energy: number;
  expected: boolean;
  /**
   * The detector's own certainty for this pitch, 0..1, when it reported one. Take
   * review uses it to point at the notes worth checking, so it is carried through
   * selection unchanged rather than re-derived from the capture score.
   */
  confidence?: number;
};

export type AudioCaptureCandidateObservation = {
  candidate: AudioCaptureCandidate;
  observedAtMs: number;
};

type AudioCaptureConsensusOptions = {
  requireRepeatedStrongest?: boolean;
  tightenAdditionalCandidates?: boolean;
};

export const normalizeMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((a, b) => a - b);

export const normalizeMidiNotesPreservingOrder = (notes: number[]) => {
  const seen = new Set<number>();
  const normalized: number[] = [];

  for (const note of notes) {
    const midi = Math.round(note);
    if (midi < 0 || midi > 127 || seen.has(midi)) continue;
    seen.add(midi);
    normalized.push(midi);
  }

  return normalized;
};

export const getAudioCaptureScore = (candidate: AudioCaptureCandidate) =>
  Math.max(0, candidate.score) +
  candidate.normalizedEnergy * 0.7 +
  candidate.energy * 0.25 +
  (candidate.expected ? 0.5 : 0);

export const selectAudioCaptureConsensusCandidates = (
  observations: AudioCaptureCandidateObservation[],
  options: AudioCaptureConsensusOptions = {},
) => {
  const candidatesByMidi = new Map<
    number,
    {
      candidate: AudioCaptureCandidate;
      count: number;
      scoreSum: number;
      maxScore: number;
      firstSeenMs: number;
      lastSeenMs: number;
    }
  >();

  for (const observation of observations) {
    const midi = Math.round(observation.candidate.midi);
    if (midi < 0 || midi > 127) continue;

    const captureScore = getAudioCaptureScore(observation.candidate);
    const existing = candidatesByMidi.get(midi);
    if (!existing) {
      candidatesByMidi.set(midi, {
        candidate: observation.candidate,
        count: 1,
        scoreSum: captureScore,
        maxScore: captureScore,
        firstSeenMs: observation.observedAtMs,
        lastSeenMs: observation.observedAtMs,
      });
      continue;
    }

    existing.count += 1;
    existing.scoreSum += captureScore;
    existing.maxScore = Math.max(existing.maxScore, captureScore);
    existing.firstSeenMs = Math.min(existing.firstSeenMs, observation.observedAtMs);
    existing.lastSeenMs = Math.max(existing.lastSeenMs, observation.observedAtMs);
    if (captureScore > getAudioCaptureScore(existing.candidate)) {
      existing.candidate = observation.candidate;
    }
  }

  const ranked = Array.from(candidatesByMidi.values()).sort((a, b) => {
    const aDurationMs = a.lastSeenMs - a.firstSeenMs;
    const bDurationMs = b.lastSeenMs - b.firstSeenMs;
    const aConsensusScore = a.scoreSum + a.maxScore + a.count * 1.5 + aDurationMs / 80;
    const bConsensusScore = b.scoreSum + b.maxScore + b.count * 1.5 + bDurationMs / 80;
    return (
      bConsensusScore - aConsensusScore ||
      b.maxScore - a.maxScore ||
      a.candidate.midi - b.candidate.midi
    );
  });

  const strongest = ranked[0];
  if (!strongest) return [];
  if (options.requireRepeatedStrongest && strongest.count < 2) return [];

  const additionalCountRatio = options.tightenAdditionalCandidates
    ? FREE_CAPTURE_ADDITIONAL_COUNT_RATIO
    : 0.35;
  const additionalScoreSumRatio = options.tightenAdditionalCandidates
    ? FREE_CAPTURE_ADDITIONAL_SCORE_SUM_RATIO
    : 0.35;
  const additionalMaxScoreRatio = options.tightenAdditionalCandidates
    ? FREE_CAPTURE_ADDITIONAL_MAX_SCORE_RATIO
    : 0.45;
  const minAdditionalCountBase = options.tightenAdditionalCandidates ? 3 : 2;
  const minCount =
    strongest.count >= 3
      ? Math.max(minAdditionalCountBase, Math.ceil(strongest.count * additionalCountRatio))
      : strongest.count;
  const minScoreSum = strongest.scoreSum * additionalScoreSumRatio;
  const minMaxScore = strongest.maxScore * additionalMaxScoreRatio;

  return ranked
    .filter((entry, index) => {
      if (index === 0) return true;
      if (entry.candidate.expected) return true;
      if (
        options.tightenAdditionalCandidates &&
        entry.firstSeenMs > strongest.firstSeenMs + FREE_CAPTURE_ADDITIONAL_FIRST_SEEN_GRACE_MS
      ) {
        return false;
      }
      return (
        entry.count >= minCount && entry.scoreSum >= minScoreSum && entry.maxScore >= minMaxScore
      );
    })
    .map((entry) => entry.candidate)
    .slice(0, MAX_AUDIO_CAPTURE_NOTES);
};

export const hasAudioCaptureOnsetEvidence = (frame: RecognitionFrame) => {
  const onsetSet = new Set(normalizeMidiNotes(frame.onsetMidiNotes ?? []));
  return (
    onsetSet.size > 0 ||
    (frame.detectedNotes ?? []).some((note) => note.onset) ||
    (frame.selectedNotes ?? []).some((note) => note.onset)
  );
};

const hasUsableFreeCaptureSignal = (frame: RecognitionFrame) => {
  if (!Number.isFinite(frame.rms)) return true;

  const rms = Math.max(0, frame.rms ?? 0);
  const minRms = Number.isFinite(frame.minRms) ? Math.max(0, frame.minRms ?? 0) : 0;
  const noiseFloor = Number.isFinite(frame.noiseFloor) ? Math.max(0, frame.noiseFloor ?? 0) : 0;
  const signalFloor = Math.max(minRms, noiseFloor * FREE_CAPTURE_MIN_NOISE_FLOOR_RATIO);

  return signalFloor <= 0 || rms >= signalFloor;
};

export const selectAudioCaptureCandidates = (
  frame: RecognitionFrame,
  options: { allowFreeCaptureSustainSelection?: boolean } = {},
): AudioCaptureCandidate[] => {
  const onsetSet = new Set(normalizeMidiNotes(frame.onsetMidiNotes ?? []));
  const hasOnsetEvidence = hasAudioCaptureOnsetEvidence(frame);
  if (frame.recognitionMode === 'free-capture') {
    if (!hasOnsetEvidence && !options.allowFreeCaptureSustainSelection) return [];
    if (!hasUsableFreeCaptureSignal(frame)) return [];

    const sparseSelectedCandidates = (frame.selectedNotes ?? [])
      .map((note: RecognitionDetectedNote) => ({
        midi: Math.round(note.midi),
        score: note.score,
        normalizedEnergy: note.normalizedEnergy,
        energy: note.energy,
        expected: note.expected,
        confidence: note.confidence,
      }))
      .filter((note) => note.midi >= 0 && note.midi <= 127);

    return sparseSelectedCandidates
      .sort(
        (a, b) =>
          Number(b.expected) - Number(a.expected) ||
          getAudioCaptureScore(b) - getAudioCaptureScore(a) ||
          b.normalizedEnergy - a.normalizedEnergy ||
          a.midi - b.midi,
      )
      .slice(0, MAX_AUDIO_CAPTURE_NOTES);
  }

  const detectedCandidates = (frame.detectedNotes ?? [])
    .filter((note) => note.onset || onsetSet.has(note.midi))
    .map((note: RecognitionDetectedNote) => ({
      midi: Math.round(note.midi),
      score: note.score,
      normalizedEnergy: note.normalizedEnergy,
      energy: note.energy,
      expected: note.expected,
      confidence: note.confidence,
    }))
    .filter((note) => note.midi >= 0 && note.midi <= 127);

  const candidatesByMidi = new Map<number, AudioCaptureCandidate>();
  for (const candidate of detectedCandidates) {
    const existing = candidatesByMidi.get(candidate.midi);
    if (!existing || getAudioCaptureScore(candidate) > getAudioCaptureScore(existing)) {
      candidatesByMidi.set(candidate.midi, candidate);
    }
  }

  const ranked = Array.from(candidatesByMidi.values()).sort(
    (a, b) =>
      Number(b.expected) - Number(a.expected) ||
      getAudioCaptureScore(b) - getAudioCaptureScore(a) ||
      b.normalizedEnergy - a.normalizedEnergy ||
      a.midi - b.midi,
  );

  const strongest = ranked[0];
  if (!strongest) return [];

  const strongestScore = getAudioCaptureScore(strongest);
  const strongestEnergy = Math.max(strongest.normalizedEnergy, 0.001);

  return ranked
    .filter((candidate, index) => {
      if (index === 0) return true;
      if (candidate.expected) return true;
      return (
        getAudioCaptureScore(candidate) >= strongestScore * AUDIO_CAPTURE_RELATIVE_SCORE_RATIO &&
        candidate.normalizedEnergy >= strongestEnergy * AUDIO_CAPTURE_RELATIVE_ENERGY_RATIO
      );
    })
    .slice(0, MAX_AUDIO_CAPTURE_NOTES);
};

export const rankAudioCaptureNotes = (notes: number[], noteScores?: Map<number, number>) =>
  normalizeMidiNotesPreservingOrder(notes)
    .sort((a, b) => (noteScores?.get(b) ?? 0) - (noteScores?.get(a) ?? 0) || a - b)
    .slice(0, MAX_AUDIO_CAPTURE_NOTES);

/**
 * The detector certainty to record for each captured pitch. Notes without a reported
 * confidence are left out: an unknown certainty must not be stored as a low one.
 */
export const getAudioCaptureConfidences = (candidates: AudioCaptureCandidate[]) => {
  const confidences = new Map<number, number>();
  for (const candidate of candidates)
    if (typeof candidate.confidence === 'number' && Number.isFinite(candidate.confidence))
      confidences.set(candidate.midi, Math.max(0, Math.min(1, candidate.confidence)));
  return confidences;
};
